import { appendAcceptHeader, RestRequestBuilder } from "@frui.ts/apiclient";
import { deserialize, plainToClass } from "class-transformer";
import type { ClassType } from "class-transformer/ClassTransformer";
import type { IPagedResults, IResults } from "./types";

const acceptContentType = "application/json,text/json";

export default class DeserializingRequestBuilder extends RestRequestBuilder {
  getEntities<T>(returnType: ClassType<T>, queryParams?: any): Promise<IResults<T>> {
    const requestUrl = this.appendQuery(this.url, queryParams);
    const params = appendAcceptHeader(this.params, acceptContentType);
    return this.apiConnector
      .get(requestUrl, params)
      .then(x => x.json())
      .then(x => {
        return {
          items: x.items.map((item: any) => plainToClass(returnType, item)),
        };
      });
  }

  getPagedEntities<T>(returnType: ClassType<T>, queryParams?: any): Promise<IPagedResults<T>> {
    const requestUrl = this.appendQuery(this.url, queryParams);
    const params = appendAcceptHeader(this.params, acceptContentType);
    return this.apiConnector
      .get(requestUrl, params)
      .then(x => x.json())
      .then(x => {
        const { items, ...rest } = x;
        return {
          ...rest,
          items: items.map((item: any) => plainToClass(returnType, item)),
        };
      });
  }

  getEntity<T>(returnType: ClassType<T>, queryParams?: any): Promise<T> {
    const requestUrl = this.appendQuery(this.url, queryParams);
    const params = appendAcceptHeader(this.params, acceptContentType);
    return this.apiConnector
      .get(requestUrl, params)
      .then(x => x.text())
      .then(x => deserialize(returnType, x));
  }

  postEntity<T>(content: any, returnType: ClassType<T>): Promise<T> {
    const params = appendAcceptHeader(this.params, acceptContentType);
    return this.apiConnector
      .postJson(this.url, content, params)
      .then(x => x.text())
      .then(x => deserialize(returnType, x));
  }

  postWithoutResponse(content: any): Promise<void> {
    const params = appendAcceptHeader(this.params, acceptContentType);
    return this.apiConnector.postJson(this.url, content, params).then(() => undefined);
  }

  postEntities<T>(content: any, returnType: ClassType<T>, signal?: AbortSignal, queryParams?: any): Promise<IResults<T>> {
    const params = appendAcceptHeader(this.params, acceptContentType);
    if (signal) {
      params.signal = signal;
    }

    return (
      this.apiConnector
        .postJson(this.url, content, params)
        .then(x => x.json())
        // eslint-disable-next-line sonarjs/no-identical-functions
        .then(x => {
          return {
            items: x.items.map((item: any) => plainToClass(returnType, item)),
          };
        })
    );
  }

  postPagedEntities<T>(
    content: any,
    returnType: ClassType<T>,
    signal?: AbortSignal,
    queryParams?: any
  ): Promise<IPagedResults<T>> {
    const requestUrl = this.appendQuery(this.url, queryParams);
    const params = appendAcceptHeader(this.params, acceptContentType);
    if (signal) {
      params.signal = signal;
    }

    return (
      this.apiConnector
        .postJson(requestUrl, content, params)
        .then(x => x.json())
        // eslint-disable-next-line sonarjs/no-identical-functions
        .then(x => {
          const { items, ...rest } = x;
          return {
            ...rest,
            items: items.map((item: any) => plainToClass(returnType, item)),
          };
        })
    );
  }

  postWithQuery<T>(content: any, returnType: ClassType<T>, signal?: AbortSignal, queryParams?: any): Promise<T> {
    const requestUrl = this.appendQuery(this.url, queryParams);
    const params = appendAcceptHeader(this.params, acceptContentType);
    if (signal) {
      params.signal = signal;
    }

    return (
      this.apiConnector
        .postJson(requestUrl, content, params)
        .then(x => x.json())
        // tslint:disable-next-line:no-identical-functions
        .then(x => {
          return plainToClass(returnType, x);
        })
    );
  }

  putEntity<T>(content: any, returnType: ClassType<T>): Promise<T> {
    const params = appendAcceptHeader(this.params, acceptContentType);
    return this.apiConnector
      .putJson(this.url, content, params)
      .then(x => x.text())
      .then(x => deserialize(returnType, x));
  }

  patchEntity<T>(content: any, returnType: ClassType<T>): Promise<T> {
    const params = appendAcceptHeader(this.params, acceptContentType);
    return this.apiConnector
      .patchJson(this.url, content, params)
      .then(x => x.text())
      .then(x => deserialize(returnType, x));
  }

  delete(content?: any, queryParams?: any) {
    const requestUrl = this.appendQuery(this.urlValue, queryParams);
    return content
      ? this.apiConnector.deleteJson(requestUrl, content, this.params)
      : this.apiConnector.delete(requestUrl, undefined, this.params);
  }
}
